import React from "react";

import { ChevronIcon } from "./Icons";

const SWIPE_PX = 40;

//Photo slideshow with arrows and dots (or a "2 / 5" counter), as on Wix. Can move on by itself, but pauses while the
//visitor hovers or tabs into it, and never for visitors who prefer reduced motion. Swipe left / right on phones
const Slideshow = ({ photos, label, className = "", autoplayMs = 0, showCounter = false }) => {
  const [index, setIndex] = React.useState(0);
  const [isPaused, setIsPaused] = React.useState(false);
  const touchX = React.useRef(null);
  const reducedMotion = React.useMemo(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches, []);
  const count = photos.length;

  const go = (i) => setIndex((i + count) % count);

  React.useEffect(() => {
    if (!autoplayMs || isPaused || reducedMotion || count < 2) {
      return undefined;
    }
    const timer = window.setTimeout(() => setIndex((i) => (i + 1) % count), autoplayMs);
    return () => window.clearTimeout(timer);
  }, [index, isPaused, reducedMotion, count, autoplayMs]);

  return (
    <div
      className={`slideshow ${className}`}
      role={"region"}
      aria-roledescription={"carousel"}
      aria-label={label}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
      onTouchStart={(e) => {
        touchX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchX.current === null) {
          return;
        }
        const dx = e.changedTouches[0].clientX - touchX.current;
        touchX.current = null;
        if (Math.abs(dx) > SWIPE_PX) {
          go(index + (dx < 0 ? 1 : -1));
        }
      }}
    >
      {photos.map((photo, i) => (
        <div
          key={photo.src}
          className={`slideshow-slide ${i === index ? "is-active" : ""}`}
          aria-hidden={i !== index}
          aria-roledescription={"slide"}
          aria-label={`${i + 1} of ${count}`}
        >
          <img
            src={photo.src}
            alt={photo.alt}
            loading={i === 0 ? undefined : "lazy"}
          />
        </div>
      ))}

      {count > 1 ? (
        <React.Fragment>
          <button
            type={"button"}
            className={"slideshow-arrow is-prev"}
            aria-label={"Previous photo"}
            onClick={() => go(index - 1)}
          >
            <ChevronIcon direction={"left"} />
          </button>
          <button
            type={"button"}
            className={"slideshow-arrow is-next"}
            aria-label={"Next photo"}
            onClick={() => go(index + 1)}
          >
            <ChevronIcon />
          </button>
          {showCounter ? (
            <p
              className={"slideshow-counter"}
              aria-live={"polite"}
            >
              {index + 1} / {count}
            </p>
          ) : (
            <div className={"slideshow-dots"}>
              {photos.map((photo, i) => (
                <button
                  key={photo.src}
                  type={"button"}
                  className={`slideshow-dot ${i === index ? "is-active" : ""}`}
                  aria-label={`Show photo ${i + 1}`}
                  aria-current={i === index ? "true" : undefined}
                  onClick={() => go(i)}
                />
              ))}
            </div>
          )}
        </React.Fragment>
      ) : null}
    </div>
  );
};

export default Slideshow;
